import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, Truck, ArrowRight } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        setData(res.data);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
      } catch (e) {}
      attempts.current += 1;
      if (attempts.current >= 8) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-ksy-bg px-5">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 p-8 md:p-10 text-center" data-testid="payment-success-page">
        <Link to="/" className="inline-flex items-center gap-2 text-ksy-navy font-display font-extrabold text-xl mb-6">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-ksy-navy text-white"><Truck className="w-5 h-5" /></span> KS&amp;Y Transport
        </Link>

        {state === "checking" && (
          <div className="py-8" data-testid="payment-checking">
            <Loader2 className="w-12 h-12 text-ksy-blue animate-spin mx-auto mb-4" />
            <p className="text-slate-500">{t("pay.checking")}</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-display font-extrabold text-2xl md:text-3xl text-ksy-navy">{t("pay.success_title")}</h1>
            <p className="mt-2 text-slate-500">{t("pay.success_sub")}</p>
            {data && (
              <div className="mt-6 text-left bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-2.5">
                <Line label={t("pay.route")} value={`${data.origin} → ${data.destination}`} />
                <Line label={t("pay.vehicles")} value={data.num_cars} />
                <Line label={t("pay.distance")} value={`${Number(data.miles).toLocaleString()} mi`} />
                <div className="flex justify-between border-t border-slate-200 pt-3">
                  <span className="font-bold text-ksy-navy">{t("pay.paid")}</span>
                  <span className="font-display font-extrabold text-xl text-green-600">{money(data.total)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-display font-extrabold text-2xl text-ksy-navy">{t("pay.failed")}</h1>
          </div>
        )}

        <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-ksy-blue text-white font-bold px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors" data-testid="payment-home-link">
          {t("pay.home")} <ArrowRight className="w-4 h-4" />
        </Link>
      </motion.div>
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-ksy-navy">{value}</span>
    </div>
  );
}
